import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { ChevronLeft, ChevronRight, FileText, Info, Loader2, Play, CheckCircle } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Form Details Modal Component
const FormDetailsModal = ({ selectedForm, auditFormData, isLoadingFormData, formDataError }) => {
  const { t } = useTranslation();
  if (isLoadingFormData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>{t('category.loading_form_details')}</span>
      </div>
    );
  }

  if (formDataError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-2">{t('category.error_loading_details')}</div>
        <div className="text-sm text-gray-500">{formDataError}</div>
      </div>
    );
  }

  if (!auditFormData?.data) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500">{t('category.no_detailed_data')}</div>
      </div>
    );
  }

  const formDetails = auditFormData.data;

  return (
    <ScrollArea className="max-h-[60vh] pr-4">
      <div className="space-y-4">
        {/* Basic Form Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.form_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name || t('summary_item.na')}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.form_code')}</label>
            <p className="text-lg font-semibold">{formDetails.form_code || t('summary_item.na')}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.italian_name')}</label>
            <p className="text-lg font-semibold">{formDetails.form_name_italian || t('summary_item.na')}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.total_fields')}</label>
            <p className="text-lg font-semibold">{formDetails.total_fields_count || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.total_points')}</label>
            <p className="text-lg font-semibold">{formDetails.total_possible_points || 0}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">{t('category.passing_threshold')}</label>
            <p className="text-lg font-semibold">{formDetails.passing_threshold || 0}%</p>
          </div>
        </div>

        {/* Production Line Information */}
        {formDetails.production_line && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">{t('category.production_line')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.line_name')}</label>
                <p className="font-medium">{formDetails.production_line.line_name || t('summary_item.na')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.line_id')}</label>
                <p className="font-medium">{formDetails.production_line.line_id || t('summary_item.na')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product Information */}
        {formDetails.product && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">{t('category.product')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.product_name')}</label>
                <p className="font-medium">{formDetails.product.product_name || t('summary_item.na')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('category.model_number')}</label>
                <p className="font-medium">{formDetails.product.model_number || t('summary_item.na')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        {formDetails.form_structure && formDetails.form_structure.length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-3">{t('category.categories')} ({formDetails.form_structure.length})</h3>
            <div className="space-y-2">
              {formDetails.form_structure.map((category, index) => (
                <div key={category.id || index} className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{category.title}</p>
                      {category.categoryData?.category_code_it && (
                        <p className="text-sm text-gray-600">{category.categoryData.category_code_it}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="ml-2">
                        {category.fields?.length || 0} {t('category.items')}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status and Dates */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-2">{t('category.status_dates')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">{t('status.label')}</label>
              <Badge variant={formDetails.is_published ? "default" : "secondary"}>
                {formDetails.is_published ? t('status.published') : t('status.draft')}
              </Badge>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">{t('category.created')}</label>
              <p className="font-medium">
                {formDetails.created_at ? new Date(formDetails.created_at).toLocaleDateString() : t('summary_item.na')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

// ControlItemsViewPage Component
export default function ControlItemsViewPage({
  onPrevious,
  onNext,
  onControlItemClick,
  onNavigate,
  selectedForm,
  auditFormData,
  isLoadingFormData,
  formDataError,
  selectedCategory,
  currentAuditResponse,  // NEW: For progress tracking
  // Score props
  currentScore = null,
  showScore = false,
}) {
  const { t } = useTranslation();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // Debug logging (reduced frequency)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Control Items Page - Props received:', {
        categoryId: selectedCategory?.originalCategory?.categoryData?.category_id,
        hasCurrentAuditResponse: !!currentAuditResponse,
        auditResponseId: currentAuditResponse?.audit_form_response_id,
        hasCumulativeData: !!currentAuditResponse?.raw_response_json?.cumulativeAuditData
      });
    }
  }, [currentAuditResponse?.audit_form_response_id, selectedCategory?.originalCategory?.categoryData?.category_id]);

  // Get category information
  const categoryInfo = selectedCategory?.originalCategory?.categoryData || {};
  const categoryTitle = selectedCategory?.name || selectedCategory?.originalCategory?.title || 'Category';
  const categoryTranslation = selectedCategory?.translation || categoryInfo.category_code_it || '';
  
  // Find the next incomplete control item WITHIN CURRENT CATEGORY ONLY
  const findNextIncompleteItemInCategory = () => {
    if (!auditFormData?.hierarchy?.control_items || !currentAuditResponse?.raw_response_json?.cumulativeAuditData) {
      return null;
    }

    const cumulativeData = currentAuditResponse.raw_response_json.cumulativeAuditData;
    const categoryProgressMap = currentAuditResponse.raw_response_json.categoryProgressMap || {};
    const categoryId = selectedCategory?.originalCategory?.categoryData?.category_id;

    if (!categoryId) return null;

    const categoryData = cumulativeData[categoryId];
    const categoryProgress = categoryProgressMap[categoryId];

    // Get control items for THIS category only
    const categoryControlItems = auditFormData.hierarchy.control_items.filter(
      item => item.category_id === categoryId
    );

    // If we have saved progress, start from there
    const startIndex = categoryProgress?.currentControlItemIndex || 0;

    for (let i = startIndex; i < categoryControlItems.length; i++) {
      const controlItem = categoryControlItems[i];
      const controlItemId = controlItem.control_item_id;
      const savedControlItemData = categoryData?.controlItems?.[controlItemId];

      // Get total checks for this control item
      const totalChecks = auditFormData.hierarchy.checks
        ?.filter(check => check.control_item_id === controlItemId).length || 0;

      // Determine if this control item is incomplete
      let isIncomplete = true;
      if (savedControlItemData?.checks && totalChecks > 0) {
        const completedChecks = Object.values(savedControlItemData.checks)
          .filter(check => check.isCompleted === true).length;
        isIncomplete = completedChecks === 0;
      } else if (totalChecks === 0) {
        isIncomplete = false; // No checks required = automatically complete
      }

      if (isIncomplete) {
        return {
          categoryId,
          controlItemId,
          controlItem: controlItem,
          index: i
        };
      }
    }

    return null; // All items in this category complete
  };

  // Generate control items from auditFormData with real progress data
  const generateControlItems = () => {
    // Essential debug: Check if we have saved data
    const hasCumulativeData = !!currentAuditResponse?.raw_response_json?.cumulativeAuditData;
    const cumulativeDataKeys = currentAuditResponse?.raw_response_json?.cumulativeAuditData ? Object.keys(currentAuditResponse.raw_response_json.cumulativeAuditData) : [];
    console.log('🔍 ControlItems DEBUG:', { hasCumulativeData, cumulativeDataKeys, categoryId: categoryInfo.category_id });

    if (!auditFormData?.hierarchy?.control_items || !selectedCategory) {
      return [];
    }

    const categoryId = categoryInfo.category_id;
    if (!categoryId) {
      return [];
    }

    // Filter control items that belong to this category
    const categoryControlItems = auditFormData.hierarchy.control_items.filter(
      controlItem => controlItem.category_id === categoryId
    );

    return categoryControlItems.map((controlItem) => {
      const controlItemId = controlItem.control_item_id;
      const savedControlItemData = currentAuditResponse?.raw_response_json?.cumulativeAuditData?.[categoryId]?.controlItems?.[controlItemId];


      // Get total checks from hierarchy
      const totalChecks = auditFormData.hierarchy.checks
        ?.filter(check => check.control_item_id === controlItemId).length || 0;

      // CORRECTED STATUS DETERMINATION
      let status = t('status.not_started');
      let completedChecks = 0;

      if (savedControlItemData && savedControlItemData.checks) {
        // Count completed checks in this control item
        const checks = savedControlItemData.checks || {};
        const allChecks = Object.values(checks);
        completedChecks = allChecks.filter(check => check.isCompleted === true).length;


        // Determine status based on correct completion logic - at least one check selected
        if (totalChecks > 0) {
          if (completedChecks > 0) {
            status = t('status.completed');  // At least one check completed (Group A or Group B)
          } else {
            status = t('status.not_started');  // No checks completed
          }
        } else {
          // If no checks are required for this control item, it's automatically completed
          status = t('status.completed');
        }
      } else {
        // No saved data means not started
        status = t('status.not_started');

        // Special case: If no checks required, consider it completed
        if (totalChecks === 0) {
          status = t('status.completed');
        }
      }


      return {
        id: controlItemId,
        name: controlItem.control_item_code,
        translation: controlItem.control_item_code_it,
        status: status,
        total: totalChecks,
        completedChecks: completedChecks,
        originalControlItem: controlItem // Keep reference to original data
      };
    });
  };

  const controlItems = generateControlItems();
  const nextIncompleteItemInCategory = findNextIncompleteItemInCategory();

  // Helper function to check if this is the continuation item (within current category only)
  const isContinuationItem = (controlItem) => {
    return nextIncompleteItemInCategory?.controlItemId === controlItem.id;
  };

  const getStatusColor = (status) => {
    if (status === t('status.completed')) return "bg-green-100 text-green-700 border-green-200";
    if (status === t('status.draft')) return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (status === t('status.not_started')) return "bg-gray-100 text-gray-700 border-gray-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const getProgressColor = (status) => {
    if (status === t('status.completed')) return "text-green-700";
    if (status === t('status.draft')) return "text-yellow-700";
    if (status === t('status.not_started')) return "text-gray-700";
    return "text-gray-700";
  };

  return (
    <div className="pwa-flex-page bg-gray-50 mt-8">
      {/* Fixed Header */}
      <header className="pwa-fixed-header bg-white px-6 py-6 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center md:text-left">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('control_items.audit_session')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.audit_form_response_id ? `AUD-${currentAuditResponse.audit_form_response_id}` : 'AUD-NEW'}
              </strong>
            </div>
            <div className="text-center">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('control_items.work_started')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.created_at 
                  ? new Date(currentAuditResponse.created_at).toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }).replace(',', '')
                  : new Date().toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit', 
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    }).replace(',', '')
                }
              </strong>
            </div>
            <div className="text-center md:text-right">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{t('control_items.product_serial')}</span>
              <strong className="block text-lg font-bold text-gray-900 mt-1">
                {currentAuditResponse?.serial_number || selectedForm?.serial_number || t('summary_item.na')}
              </strong>
            </div>
          </div>
        </div>
      </header>

      {/* Fixed Title Section with Score */}
      <section className="bg-sky-500 px-6 py-4 shadow-lg flex-shrink-0" style={{ background: "#0098cb" }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex-shrink-0">
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center md:text-left">{t('control_items.title')}</h1>
            <p className="text-white/90 mt-1">{categoryTranslation} / {categoryTitle}</p>
          </div>

          {/* Center: Score Display */}
          {showScore && currentScore && (
            <div className="flex-1 flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20">
                <div className="text-center">
                  <span className="text-xs font-medium text-white/80 uppercase tracking-wider">{t('control_items.current_score')}</span>
                  <div className="mt-0.5 flex items-baseline justify-center gap-1">
                    <span className="text-2xl font-bold text-white">{currentScore.achieved}</span>
                    <span className="text-xs text-white/70">{t('control_items.points')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Right: Form Details Button */}
          {selectedForm && (
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 flex-shrink-0"
              onClick={() => setIsFormModalOpen(true)}
            >
              <Info className="h-4 w-4 mr-2" />
              {t('control_items.form_details')}
            </Button>
          )}
        </div>
      </section>

      {/* Fixed Form Info Section */}
      <div className="bg-white px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="max-w-2xl mx-auto">
          {/* Display selected form info */}
          {selectedForm && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700">{t('control_items.control_items_in')} {categoryTranslation} / {categoryTitle}</p>
                  </div>
                  {isLoadingFormData && (
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  )}
                </div>
              </div>

              {/* Current category continuation guidance */}
              {nextIncompleteItemInCategory && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm text-green-800 font-medium">{t('control_items.continue_incomplete')}</p>
                      <p className="text-xs text-green-700">
                        {t('control_items.next_item')} <span className="font-medium">{nextIncompleteItemInCategory.controlItem?.control_item_code_it}</span> - {t('control_items.look_for')} <span className="font-medium">"{t('control_items.continue_indicator')}"</span> indicator
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Category completed message */}
              {!nextIncompleteItemInCategory && controlItems.some(item => item.status === t('status.completed')) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">{t('control_items.category_completed')}</p>
                      <p className="text-xs text-blue-700">
                        {t('control_items.all_items_completed')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Main Content - THIS IS THE KEY CHANGE */}
      <div className="pwa-scrollable-content pwa-footer-clearance">
        <div className="h-full">
          <div className="pwa-tablet-container max-w-2xl mx-auto h-full">
            <div className="h-full pwa-content-padding pwa-no-horizontal-scroll">
              {/* Loading state for control items */}
              {isLoadingFormData && !auditFormData && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                    <span className="text-gray-600">{t('control_items.loading_control_items')}</span>
                  </div>
                </div>
              )}

              {/* Error state */}
              {formDataError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="text-red-800 font-medium">{t('control_items.error_loading_form')}</div>
                  <div className="text-red-600 text-sm mt-1">{formDataError}</div>
                </div>
              )}

              {/* Control Items List - SCROLLABLE CONTENT */}
              {!isLoadingFormData && controlItems.length > 0 && (
                <div className="h-full overflow-y-auto">
                  <div className="space-y-4 pb-20">
                    {controlItems.map((controlItem, index) => (
                      <button
                        key={controlItem.id}
                        type="button"
                        className={cn(
                          "w-full text-left bg-white rounded-lg shadow-sm border border-gray-200 p-6",
                          "transition-all duration-200 outline-none opacity-0 animate-[slideInStaggered_0.3s_ease-out_forwards]",
                          // Clickable: completed items OR continuation item
                          (controlItem.status === "Completed" || isContinuationItem(controlItem))
                            ? "cursor-pointer hover:bg-sky-100 hover:border-sky-400 hover:shadow-md focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
                            : "cursor-not-allowed opacity-60 bg-gray-50",
                          // Special styling for continuation item
                          isContinuationItem(controlItem) && controlItem.status !== "Completed"
                            ? "border-blue-300 bg-blue-50 ring-2 ring-blue-200"
                            : ""
                        )}
                        style={{ animationDelay: `${index * 0.05}s` }}
                        onClick={() => {
                          // Clickable: completed items OR continuation item
                          if (controlItem.status === "Completed" || isContinuationItem(controlItem)) {
                            // Pass both control item and current category context
                            const categoryContext = {
                              category_id: categoryInfo.category_id,
                              category_name: selectedCategory?.name || categoryTitle,
                              category_translation: selectedCategory?.translation || categoryTranslation,
                              category_code: categoryInfo.category_code || categoryTitle
                            };
                            onControlItemClick?.(controlItem, categoryContext);
                          }
                        }}
                        disabled={!(controlItem.status === "Completed" || isContinuationItem(controlItem))}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h2 className="text-xl font-bold text-gray-900">{controlItem.translation}</h2>
                              {isContinuationItem(controlItem) && controlItem.status !== t('status.completed') && (
                                <Badge variant="default" className="bg-blue-600 text-white border-blue-600 px-2 py-1">
                                  <Play className="h-3 w-3 mr-1" />
                                  {t('control_items.continue_indicator')}
                                </Badge>
                              )}
                            </div>
                            {controlItem.name && <p className="text-gray-600 text-sm">{controlItem.name}</p>}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge variant="outline" className={cn("px-3 py-1 rounded-full font-medium", getStatusColor(controlItem.status))}>
                              {controlItem.status}
                            </Badge>
                            {/* Commented out: Checks counter */}
                            {/* <span className={cn("text-sm font-medium", getProgressColor(controlItem.status))}>
                              {controlItem.completedChecks || 0}/{controlItem.total} {t('control_items.checks')}
                            </span> */}
                            {/* Commented out: Visual progress indicator */}
                            {/* {controlItem.total > 0 && (
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full transition-all duration-300",
                                    controlItem.status === t('status.completed') ? "bg-green-500" :
                                    controlItem.status === t('status.draft') ? "bg-yellow-500" : "bg-gray-300"
                                  )}
                                  style={{
                                    width: `${Math.round(((controlItem.completedChecks || 0) / controlItem.total) * 100)}%`
                                  }}
                                />
                              </div>
                            )} */}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isLoadingFormData && !formDataError && controlItems.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-40" />
                    <h3 className="text-lg font-medium mb-2">{t('control_items.no_available')}</h3>
                    <p className="text-sm">{t('control_items.no_found_category')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <footer className="pwa-fixed-footer pwa-footer-padding bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto flex justify-between items-center pwa-footer-button-spacing">
          <Button variant="outline" onClick={onPrevious} className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4" />
            {t('control_items.back_to_categories')}
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => {
              console.log('🔥 ControlItemsViewPage - Summary button clicked!', { onNavigate: !!onNavigate });
              if (onNavigate) {
                console.log('🔥 Calling onNavigate with auditSummary');
                onNavigate('auditSummary');
              } else {
                console.error('❌ onNavigate is not defined!');
              }
            }} className="flex items-center gap-2">
              {t('nav.summary')}
            </Button>
            <Button onClick={onNext} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
              {t('control_items.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </footer>

      {/* Form Details Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedForm?.title} - {t('control_items.details')}
            </DialogTitle>
          </DialogHeader>
          <FormDetailsModal 
            selectedForm={selectedForm}
            auditFormData={auditFormData}
            isLoadingFormData={isLoadingFormData}
            formDataError={formDataError}
          />
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes slideInStaggered {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}